import { fromJS, Map } from 'immutable';
import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

import { segmentTrack } from 'flow/actions/segment-actions';
import {
    addTagToTicket,
    removeTagFromTicket,
} from 'flow/actions/tickets-action';
import { generateSuggestedTags } from 'flow/actions/ticket-actions';
import { useTicketCallSuggestedTags } from 'flow/hooks/api/useTicketCallSuggestedTags';
import RobotIcon from 'flow/components/tickets/RobotIcon';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Skeleton from 'flow/components/ui-components/Skeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { selectTagsTicketIsTaggedWith } from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { SuggestedTagDTO, Tag } from 'flow/types/Tag';
import { TypedMap } from 'flow/types/TypedMap';
import { listToMap } from 'flow/utils';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { twMerge } from 'tailwind-merge';
import SuggestedTag from './SuggestedTag';
import useTicketAction from 'flow/hooks/useTicketAction';

interface Props {
    ticketKey?: TicketKey;
    transcriptOnly?: boolean;
}

const TicketTagsModal = ({ ticketKey, transcriptOnly }: Props) => {
    const { i18next } = useAppContext();

    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const [isFetching, setIsFetching] = useState(false);
    const [tagsSuggested, setTagsSuggested] = useState<any>(Map());
    const [tagsSelected, setTagsSelected] = useState<any>(Map());
    const [labelsOfTagsInRequestState, setLabelsOfTagsInRequestState] =
        useState(Map());
    const tagsInTicketByKey = useAppSelector((state) =>
        selectTagsTicketIsTaggedWith(state, ticketKey),
    );

    // Tags the call's extended transcription already carries, if it has any.
    const tagsFromExtendedTranscription = useTicketCallSuggestedTags(ticketKey);

    useEffect(() => {
        // Prefer the extended transcription's own tags: the pipeline derives them alongside the
        // summary, and generateSuggestedTags can't produce any for such a call anyway — it builds
        // them from the legacy transcript, which an extended-transcription call doesn't have.
        if (tagsFromExtendedTranscription) {
            setTagsSuggested(
                listToMap(
                    fromJS(tagsFromExtendedTranscription),
                    (tag: TypedMap<SuggestedTagDTO>) => tag.get('label'),
                ),
            );
            setIsFetching(false);
            return;
        }

        setIsFetching(true);
        dispatch(generateSuggestedTags(ticketKey, transcriptOnly))
            .then((response: SuggestedTagDTO[]) => {
                const responseMassaged = listToMap(
                    fromJS(response),
                    (tag: TypedMap<SuggestedTagDTO>) => tag.get('label'),
                );
                setTagsSuggested(responseMassaged);
                setIsFetching(false);
            })
            .catch(() => {
                setIsFetching(false);
            });
    }, [dispatch, ticketKey, transcriptOnly, tagsFromExtendedTranscription]);

    useEffect(() => {
        tagsSuggested?.forEach((tagSuggested: TypedMap<SuggestedTagDTO>) => {
            const tagSuggestedIsAlreadyInTicket = tagsInTicketByKey?.find(
                (tag: TypedMap<Tag>) =>
                    tag.get('label') === tagSuggested.get('label'),
            );

            if (
                tagSuggestedIsAlreadyInTicket &&
                !tagsSelected.has(tagSuggested.get('label'))
            ) {
                setTagsSelected(
                    tagsSelected.set(tagSuggested.get('label'), tagSuggested),
                );
            }
        });
    }, [tagsSuggested, tagsInTicketByKey, tagsSelected]);

    const onClickTag = useCallback(
        (_: React.MouseEvent<HTMLButtonElement, MouseEvent>, label: string) => {
            setLabelsOfTagsInRequestState(
                labelsOfTagsInRequestState.set(label, label),
            );
            if (tagsSelected.has(label)) {
                dispatch(
                    segmentTrack(TICKET_STRINGS.TAGS_SUGGESTED_REMOVED, {
                        component: 'Suggested Tags',
                    }),
                );
                // Take the key from the ticket's own tags: the action would otherwise look the
                // label up in the org's available tags, which are only fetched by the ticket
                // details tab — so a tag added before this modal opened has no key to remove by.
                const keyOfTagToRemove = tagsInTicketByKey
                    ?.find((tag: TypedMap<Tag>) => tag.get('label') === label)
                    ?.get('key');

                runTicketAction(
                    removeTagFromTicket(ticketKey, keyOfTagToRemove, label),
                )
                    .then(() => {
                        setTagsSelected(tagsSelected.delete(label));
                    })
                    .finally(() => {
                        setLabelsOfTagsInRequestState(
                            labelsOfTagsInRequestState.delete(label),
                        );
                    });
            } else {
                dispatch(
                    segmentTrack(TICKET_STRINGS.TAGS_SUGGESTED_ADDED, {
                        component: 'Suggested Tags',
                    }),
                );

                const tag = tagsSuggested.get(label);
                const source = tagsSuggested.get(label)?.get('source');
                runTicketAction(
                    addTagToTicket(ticketKey, label, undefined, source),
                )
                    .then(() => {
                        setTagsSelected(tagsSelected.set(label, tag));
                    })
                    .finally(() => {
                        setLabelsOfTagsInRequestState(
                            labelsOfTagsInRequestState.delete(label),
                        );
                    });
            }
        },
        [
            dispatch,
            labelsOfTagsInRequestState,
            tagsInTicketByKey,
            tagsSelected,
            ticketKey,
            tagsSuggested,
        ],
    );

    return (
        <BaseModal
            open={true}
            header={
                <span className="ps-3 text-lg text-gray-800">
                    {i18next.t('ai.assistant')}
                </span>
            }
            onClose={() => {}}
            disablePadding={true}
            disableActions={true}
            className="w-[600px]"
        >
            <div className="flex min-h-52 flex-col p-[26px]">
                <div
                    className={twMerge(
                        'flex h-6 gap-x-3',
                        isFetching ? 'animate-fade' : '',
                    )}
                >
                    <div
                        className={twMerge(
                            'size-6 fill-gray-600',
                            isFetching ? 'animate-spinSmoothAcceleration' : '',
                        )}
                    >
                        <RobotIcon />
                    </div>
                    <div className="flex items-end text-sm font-bold">
                        {isFetching
                            ? i18next.t('tags.generating')
                            : i18next.t('tags.suggested.colon')}
                    </div>
                </div>
                <div className="flex h-full flex-col px-9">
                    {tagsSuggested ? (
                        <div className="my-4 flex flex-wrap gap-2">
                            {tagsSuggested
                                .map((tag: TypedMap<SuggestedTagDTO>) => {
                                    const tagText = tag.get('label');
                                    return (
                                        <SuggestedTag
                                            onClick={onClickTag}
                                            text={tagText}
                                            key={`tm-st-${tagText}`}
                                            isLoading={labelsOfTagsInRequestState.has(
                                                tagText,
                                            )}
                                            isSelected={tagsSelected.has(
                                                tagText,
                                            )}
                                            disabled={labelsOfTagsInRequestState.has(
                                                tagText,
                                            )}
                                        />
                                    );
                                })
                                .valueSeq()}
                        </div>
                    ) : (
                        <div className="mt-4 rounded-sm bg-gray-100 p-2 dark:bg-gray-200">
                            <Skeleton>
                                <div className="flex flex-1 flex-col gap-2 py-1">
                                    <Skeleton.Text className="h-3 w-96" />
                                    <Skeleton.Text className="h-3 w-52" />
                                    {
                                        <div>
                                            <div className="mt-4 flex h-9 w-28 items-center justify-center rounded-sm bg-gray-200">
                                                <div className="h-3 w-3/4 rounded-sm bg-gray-300" />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </Skeleton>
                        </div>
                    )}
                    {tagsSelected && !tagsSelected.isEmpty() ? (
                        <span className="mt-2 text-xs">
                            {getTagsAddedString(i18next, tagsSelected)}
                        </span>
                    ) : null}
                </div>
            </div>
        </BaseModal>
    );
};

export default React.memo(TicketTagsModal);

const getTagsAddedString = (i18next: any, tags: any) => {
    if (!tags || tags.isEmpty()) return '';
    const tagsCommaSeparated = tags
        .map((tag: TypedMap<SuggestedTagDTO>) => tag.get('label'))
        .valueSeq()
        .join(', ');
    return tags.size === 1
        ? i18next.t('tags.ticket.added', { Tag: tagsCommaSeparated })
        : i18next.t('tags.ticket.added_plural', { Tags: tagsCommaSeparated });
};
